import React, { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import { TextField, CheckboxInput } from '@td-client-frontend/common';
import ActivitySearchSelect from './ActivitySearchSelect';
import { ActivityDateRange } from './ActivityDateRange';
import { Button } from '../../Button';
import { useGetActivityTypes } from '../../../hooks/useActivities';
import { format, subMonths } from 'date-fns';
import ActivityFilterInput, { CheckBoxItem } from './ActivityFilterInput';
import { ActivityType, PartialActivityLogSearchQuery } from '../../../@types/Audit';
import { SearchSelectItem } from '@td-client-frontend/common/src/components/form-controls';
import { systemActivityTypesTableConfigs } from '../Table/constants/SystemActivityConstants';

const CURRENT_DATE: Date = new Date();

const INITIAL_DATE: Record<'from' | 'to', Date> = {
  from: subMonths(CURRENT_DATE, 1),
  to: CURRENT_DATE
};

const INITIAL_SEARCH_QUERY_OBJECT: PartialActivityLogSearchQuery = {
  user: '',
  from: format(INITIAL_DATE.from, 'MM/dd/yyyy'),
  to: format(INITIAL_DATE.to, 'MM/dd/yyyy'),
  type: ''
};

const CheckboxTemplate: FC<CheckBoxItem> = (item: CheckBoxItem) => (
  <div className="flex flex-row items-center">
    <CheckboxInput
      colorType="blue"
      data-testid="dropdown-checkbox"
      checked={item.checked}
      className="items-center mr-3"
      name={item.value}
    />
    <label htmlFor={item.value}>{item.label}</label>
  </div>
);

export default function SystemSearchFilters({
  onSearch
}: {
  onSearch: (queryObj: PartialActivityLogSearchQuery) => void;
}) {
  const [searchQuery, setSearchQuery] = useState<PartialActivityLogSearchQuery>(INITIAL_SEARCH_QUERY_OBJECT);
  const [searchActivityType, setSearchActivityType] = useState<string>('');
  const [selectedActivityType, setSelectedActivityType] = useState<SearchSelectItem>({ label: '', value: '' });
  const [dateRangeData, setDateRangeData] = useState<Record<'from' | 'to', Date>>(INITIAL_DATE);

  const [activeFilters, setActiveFilters] = useState<CheckBoxItem[]>([]);
  const [isError, setIsError] = useState<{ user: boolean; dateRange: boolean; filters: any }>({
    user: false,
    dateRange: false,
    filters: {}
  });

  const activityTypes = useGetActivityTypes('', 'system');

  const filteredActivityTypes: ActivityType[] = useMemo(
    () => activityTypes?.types.filter(({ label }) => label.includes(searchActivityType)) || [],
    [activityTypes, searchActivityType]
  );

  const handleActivityFilterInputError = useCallback((isError: boolean, filterName: string) => {
    setIsError((prev) => ({ ...prev, filters: { ...prev.filters, [filterName]: isError } }));
  }, []);

  const handleUserNameError = (value: string) => {
    setIsError((prev) => ({ ...prev, user: !(!value || value.length > 3) }));
  };

  const onUserNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    handleUserNameError(value);
    onSearchQueryChange({ key: 'user', value });
  };

  const onActivitySearchChange = (value: string) => {
    setSearchActivityType(value);
  };

  const filtersSettings: any = useMemo(
    () => ({
      marketing: [
        {
          label: 'Profile name',
          value: 'profileName',
          checked: activeFilters.some((filter: CheckBoxItem) => filter?.value === 'profileName'),
          lengthError: 'Must be shorter than 50 and longer than 10 characters',
          validateFn: (length: number) => length >= 10 && length <= 50
        },
        {
          label: 'Organization name',
          value: 'organizationName',
          checked: activeFilters.some((filter: CheckBoxItem) => filter?.value === 'organizationName'),
          lengthError: 'Must not be empty',
          validateFn: (length: number) => length > 0
        }
      ],
      affectedUser: [
        {
          label: 'Affected user',
          value: 'affectedUser',
          checked: activeFilters.some((filter: CheckBoxItem) => filter?.value === 'affectedUser'),
          lengthError: 'Must be shorter than 50 and longer than 3 characters',
          validateFn: (length: number) => length > 3 && length < 50
        }
      ],
      members: [
        {
          label: 'First name',
          value: 'memberFirstName',
          checked: activeFilters.some((filter: CheckBoxItem) => filter?.value === 'memberFirstName'),
          lengthError: 'Must be shorter than 20 and longer than 3 characters',
          validateFn: (length: number) => length > 3 && length < 20
        },
        {
          label: 'Last name',
          value: 'memberLastName',
          checked: activeFilters.some((filter: CheckBoxItem) => filter?.value === 'memberLastName'),
          lengthError: 'Must be shorter than 20 and longer than 3 characters',
          validateFn: (length: number) => length > 3 && length < 20
        }
      ],
      roles: [
        {
          label: 'Role name',
          value: 'roleName',
          checked: activeFilters.some((filter: CheckBoxItem) => filter?.value === 'roleName'),
          lengthError: 'Must not be empty',
          validateFn: (length: number) => length > 0
        }
      ],
      empty: []
    }),
    [activeFilters]
  );

  const addedFiltersConfig: CheckBoxItem[] = useMemo(() => {
    const configKey: string =
      Object.keys(systemActivityTypesTableConfigs).find(
        (key) => selectedActivityType.value && key.includes(selectedActivityType.value)
      ) || 'default';
    return [...(filtersSettings[systemActivityTypesTableConfigs[configKey]] || [])] as CheckBoxItem[];
  }, [selectedActivityType, filtersSettings]);

  const onFilterRemove = (value: string) => {
    setActiveFilters((prev) => prev.filter((config) => config.value !== value));
  };

  const handleCheck = ({ value }: { value: string }) => {
    setActiveFilters((prev: CheckBoxItem[]) => {
      const isChecked: boolean = prev.some((config: CheckBoxItem) => config.value === value);
      if (isChecked) {
        return prev.filter((config: CheckBoxItem) => config.value !== value);
      } else {
        const filter: CheckBoxItem = addedFiltersConfig.find((config) => config?.value === value)!;
        if (filter) return [...prev, filter];
        return prev;
      }
    });
  };

  const handleSearch = () => {
    onSearch(searchQuery);
  };

  const handleActivityTypeSelect = ({ key, value, label }: SearchSelectItem) => {
    onSearchQueryChange({ key, value });
    setSearchActivityType(label);
    setSelectedActivityType({ label, value });
  };

  const onSearchQueryChange = ({ key, value }: { key: string; value: string }) => {
    setSearchQuery((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const isSearchButtonDisabled: boolean = useMemo(() => {
    const isFiltersValid = Object.values(isError.filters).every((x) => !x);
    return !searchQuery.type || isError.dateRange || isError.user || !isFiltersValid;
  }, [isError.dateRange, isError.filters, isError.user, searchQuery.type]);

  return (
    <div data-testid="searchFiltersForm" className="flex flex-col items-start">
      <div className="flex space-x-4">
        <ActivitySearchSelect
          onSearchChange={onActivitySearchChange}
          setSelected={handleActivityTypeSelect}
          value={selectedActivityType.value || searchActivityType}
          options={filteredActivityTypes}
          fieldWrapperWidthClass="w-80"
          wrapperClasses="w-80"
          placeholder="select employer activity"
          name="type"
          label="Activity type"
        />
        <ActivityDateRange dateRangeData={dateRangeData} onChange={setDateRangeData} onSelect={onSearchQueryChange} />
        <TextField
          name="user"
          onChange={onUserNameChange}
          value={searchQuery.user}
          aria-label="Username"
          wrapperClasses="flex flex-col w-auto mb-3"
          wrapperClassName="w-44"
          label="User name"
          isError={isError.user}
          error="Must be longer than 3 characters"
        />
        <ActivitySearchSelect
          setSelected={handleCheck}
          onSearchChange={() => {}}
          fieldWrapperWidthClass="w-64"
          wrapperClasses="w-64"
          placeholder="Select filters"
          options={addedFiltersConfig}
          name="addFilter"
          closeOnSelect={false}
          label="Add filter"
          buildTemplate={CheckboxTemplate}
        />
        <div className="flex items-start space-x-4 mb-1 mt-7">
          <Button disabled={isSearchButtonDisabled} onClick={handleSearch}>
            Search
          </Button>
        </div>
      </div>
      <div className="flex space-x-4">
        {activeFilters.length ? (
          activeFilters.map((filter) => {
            return (
              <ActivityFilterInput
                key={filter?.value}
                filter={filter}
                searchQuery={searchQuery}
                onChange={onSearchQueryChange}
                onRemove={onFilterRemove}
                onError={handleActivityFilterInputError}
              />
            );
          })
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}
